import QRCode from "qrcode";
import type { Memo } from '../api/types/memos/types';
import { useMemosJsonGenerator } from "./useMemosJsonGenerator";

// Fonction utilitaire pour générer le nom de fichier selon le format demandé
const generateMemoFilename = (memo: Memo, exportType: string): string => {
  // Nettoyer et sécuriser le nom de fichier
  const sanitizeFilename = (str: string): string => {
    return str
      .replace(/[^a-zA-Z0-9\s]/g, '') // Supprimer les caractères spéciaux
      .replace(/\s+/g, '_') // Remplacer les espaces par des underscores
      .toLowerCase()
      .trim();
  };

  let baseName: string;
  
  if (memo.title && memo.title.trim()) {
    // Utiliser le titre si disponible
    baseName = sanitizeFilename(memo.title);
  } else if (memo.triid && memo.triid.trim()) {
    // Utiliser les 3 mots du triid si pas de titre
    const triidWords = memo.triid.split(',').map(word => word.trim()).slice(0, 3);
    baseName = triidWords.join('_');
  } else {
    // Fallback sur l'UUID si ni titre ni triid
    baseName = memo.uuid;
  }

  return `${baseName}_${exportType}.json`;
};

export function useGenerators(): {
  generateJson: (data: Memo) => void;
  generateRawJson: (data: Memo) => void;
  generateQRCodeUUID: (uuid: string) => Promise<void>;
  generateQRCodeMEMoS: (memoData: Memo) => Promise<void>;
} {
  const { generateMemosJson } = useMemosJsonGenerator();

  const generateJson = (data: Memo): void => {
    try {
      const jsonData = generateMemosJson(data);
      const filteredData = filterNonEmptyFields(jsonData);
      const jsonString = JSON.stringify(filteredData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = generateMemoFilename(data, 'memos');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors de la génération du JSON :", error);
    }
  };

  const generateRawJson = (data: Memo): void => {
    try {
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = generateMemoFilename(data, 'anamnesis');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors de la génération du JSON brut :", error);
    }
  };

  const generateQRCodeUUID = async (uuid: string): Promise<void> => {
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(uuid);
      const a = document.createElement('a');
      a.href = qrCodeDataUrl;
      a.download = 'QRCode_UUID.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Erreur lors de la génération du QRCode UUID :", error);
    }
  };

  const generateQRCodeMEMoS = async (memoData: Memo): Promise<void> => {
    try {
      const jsonData = generateMemosJson(memoData);
      // QR code: JSON compact (gain de place, clés complètes)
      const jsonString = JSON.stringify(jsonData);
      const qrCodeDataUrl = await QRCode.toDataURL(jsonString);
      const a = document.createElement('a');
      a.href = qrCodeDataUrl;
      a.download = 'QRCode_MEMoS.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Erreur lors de la génération du QRCode MEMoS :", error);
    }
  };

  const filterNonEmptyFields = (data: Record<string, unknown> | unknown[]): Record<string, unknown> | unknown[] => {
    if (Array.isArray(data)) {
      return data.map(item => 
        typeof item === 'object' && item !== null ? filterNonEmptyFields(item as Record<string, unknown>) : item
      ).filter(item => item !== null && item !== "");
    }

    const filteredData: Record<string, unknown> = {};
    const dataObj = data as Record<string, unknown>;
    for (const key in dataObj) {
      if (dataObj[key] !== null && dataObj[key] !== "") {
        if (typeof dataObj[key] === 'object' && dataObj[key] !== null) {
          const nestedFilteredData = filterNonEmptyFields(dataObj[key] as Record<string, unknown>);
          if (Object.keys(nestedFilteredData as Record<string, unknown>).length > 0) {
            filteredData[key] = nestedFilteredData;
          }
        } else {
          filteredData[key] = dataObj[key];
        }
      }
    }
    return filteredData;
  };

  return {
    generateJson,
    generateRawJson,
    generateQRCodeUUID,
    generateQRCodeMEMoS,
  };
} 